import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function maskEmail(email: string): string {
  const [localPart, domain] = email.split('@')
  if (!localPart || !domain) return email

  const visibleChars = Math.min(2, Math.floor(localPart.length / 2))
  const masked = `${localPart.slice(0, visibleChars)}***`
  return `${masked}@${domain}`
}

/**
 * Gets initials from a name (first letter of first two words, uppercased)
 */
export function getInitials(name: string | null | undefined): string {
  if (!name) return ''
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Formats a date string to "Month Year" format
 * @param dateString - ISO date string or null
 * @param options - Optional Intl.DateTimeFormatOptions to customize format
 * @returns Formatted date string or "—" if null
 */
export function formatDate(
  dateString: string | null,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!dateString) return '—'

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    ...options,
  }

  return new Date(dateString).toLocaleDateString('en-US', defaultOptions)
}

/**
 * Formats a currency amount with K/M suffixes
 * @param amount - Amount as string or null
 * @param options - Configuration for formatting
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: string | null,
  options: {
    fallback?: string
    prefix?: string
    decimals?: { millions?: number; thousands?: number }
  } = {}
): string {
  const { fallback = '—', prefix = '$', decimals = { millions: 1, thousands: 0 } } = options

  if (!amount) return fallback

  const num = Number.parseInt(amount, 10)
  if (Number.isNaN(num)) return fallback

  if (num >= 1_000_000) {
    return `${prefix}${(num / 1_000_000).toFixed(decimals.millions ?? 1)}M`
  }
  if (num >= 1_000) {
    return `${prefix}${(num / 1_000).toFixed(decimals.thousands ?? 0)}K`
  }
  return `${prefix}${num}`
}

/**
 * Formats file size in bytes to human-readable format
 */
export function formatFileSize(bytes: number | null): string {
  if (!bytes) return ''
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(0)} KB`
  return `${bytes} B`
}

/**
 * Formats duration in seconds to MM:SS or HH:MM:SS format
 */
export function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
